import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, HashRouter, Routes, Route } from "react-router-dom";

// Only eagerly load the default route
import Index from "./pages/Index";

// Lazy-load all other pages for code splitting
const HomepageV1 = lazy(() => import("./pages/HomepageV1"));
const HomepageV2 = lazy(() => import("./pages/HomepageV2"));
const HomepageVanishingAct = lazy(() => import("./pages/HomepageVanishingAct"));
const HomepageUnforgettable = lazy(() => import("./pages/HomepageUnforgettable"));
const Homepage3SecondAdvantage = lazy(() => import("./pages/Homepage3SecondAdvantage"));
const HomepageNewFreeQCard = lazy(() => import("./pages/HomepageNewFreeQCard"));
const HomepageFreeQCardV2 = lazy(() => import("./pages/HomepageFreeQCardV2"));
const HomepageFreeVersion1 = lazy(() => import("./pages/HomepageFreeVersion1"));
const HomepageFreeVersion2 = lazy(() => import("./pages/HomepageFreeVersion2"));
import HomepageEnterprise from "./pages/HomepageEnterprise";
import EnterprisePlan from "./pages/EnterprisePlan";
import NFCBusinessCard from "./pages/NFCBusinessCard";
import ConsultingDigitalBusinessCard from "./pages/ConsultingDigitalBusinessCard";
import InsuranceDigitalBusinessCard from "./pages/InsuranceDigitalBusinessCard";
import RealEstateDigitalBusinessCard from "./pages/RealEstateDigitalBusinessCard";
import AccountingDigitalBusinessCard from "./pages/AccountingDigitalBusinessCard";
import FinancialAdvisoryDigitalBusinessCard from "./pages/FinancialAdvisoryDigitalBusinessCard";
import LawDigitalBusinessCard from "./pages/LawDigitalBusinessCard";
const SignupConfirmation = lazy(() => import("./pages/SignupConfirmation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmbedPhoneMockup = lazy(() => import("./pages/EmbedPhoneMockup"));
const EmbedSignupForm = lazy(() => import("./pages/EmbedSignupForm"));

const queryClient = new QueryClient();

const shouldUseHashRouter =
  typeof window !== "undefined" && window.location.hash.startsWith("#/");

const Router = shouldUseHashRouter ? HashRouter : BrowserRouter;

// Minimal loading fallback to prevent CLS
const PageFallback = () => (
  <div style={{ minHeight: "100vh", background: "hsl(var(--background))" }} />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Router>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/homepage-v1" element={<HomepageV1 />} />
            <Route path="/homepage-v2" element={<HomepageV2 />} />
            <Route path="/homepage-vanishing-act" element={<HomepageVanishingAct />} />
            <Route path="/homepage-unforgettable" element={<HomepageUnforgettable />} />
            <Route path="/homepage-3-second-advantage" element={<Homepage3SecondAdvantage />} />
            <Route path="/homepage-new-free-qcard" element={<HomepageNewFreeQCard />} />
            <Route path="/homepage-free-qcardv2" element={<HomepageFreeQCardV2 />} />
            <Route path="/homepage-free-version1" element={<HomepageFreeVersion1 />} />
            <Route path="/homepage-free-version2" element={<HomepageFreeVersion2 />} />
            <Route path="/homepage-enterprise" element={<HomepageEnterprise />} />
            <Route path="/enterprise-plan" element={<EnterprisePlan />} />
            <Route path="/nfc-business-card" element={<NFCBusinessCard />} />
            <Route path="/consulting-digital-business-card" element={<ConsultingDigitalBusinessCard />} />
            <Route path="/insurance-digital-business-card" element={<InsuranceDigitalBusinessCard />} />
            <Route path="/real-estate-digital-business-card" element={<RealEstateDigitalBusinessCard />} />
            <Route path="/accounting-digital-business-card" element={<AccountingDigitalBusinessCard />} />
            <Route path="/financial-advisory-digital-business-card" element={<FinancialAdvisoryDigitalBusinessCard />} />
            <Route path="/law-digital-business-card" element={<LawDigitalBusinessCard />} />
            <Route path="/signup-confirmation" element={<SignupConfirmation />} />
            {/* Lightweight embed routes for Webflow iframes */}
            <Route path="/embed/phone-mockup" element={<EmbedPhoneMockup />} />
            <Route path="/embed/signup-form" element={<EmbedSignupForm />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
